// components/WeeklyAgenda.js - Vista de agenda semanal con recordatorios por técnico (100% RESPONSIVE)
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Icons } from '../utils/icons';
import { authService } from '../services/authService';
import { useTecnicos } from '../hooks/useTecnicos';
import { DIAS_SEMANA } from '../services/tecnicosService';
import MapComponent from './MapComponent';
import WhatsAppDirect from './WhatsAppDirect';
import WhatsAppQuickButtons, { WhatsAppSingleButton } from './WhatsAppQuickButtons';
import { ordenarOrdenesPorDistancia, formatearDistancia } from '../utils/distanceUtils';

// ✅ FUNCIÓN AÑADIDA: Normalización consistente de fechas sin timezone
const obtenerFechaLocal = (fecha) => {
  if (!fecha) return null;
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return null;
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ✅ AÑADIDO: IDs de técnicos definidos globalmente
const tecnicoIds = {
  Benito: 21936,
  Pablo: 23415,
  Dani: 21945,
  Ivan: 31171
};

// 📱 Teléfonos WhatsApp de técnicos (actualizar con números reales)
const tecnicoTelefonos = {
  Benito: '34649897241',
  Pablo:  '34613269705',
  Dani:   '34649897242',
  Ivan:   '34722126672',
};

// 🆕 FUNCIÓN AUXILIAR: Obtener nombre del técnico por ID
const getTechnicianNameById = (assigneeId, agentId) => {
  const id = assigneeId || agentId;
  
  for (const [nombre, tecnicoId] of Object.entries(tecnicoIds)) {
    if (tecnicoId === id) {
      return nombre;
    }
  }
  
  return 'Técnico asignado';
};

// 🆕 FUNCIÓN: Generar mensaje WhatsApp con listado de ruta y enlace Google Maps
const generarMensajeRuta = (tecnico, fecha, ordenes, ordenesGeodificadas) => {
  const fechaFormateada = new Date(fecha + 'T12:00:00').toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long'
  });
  const fechaCapitalizada = fechaFormateada.charAt(0).toUpperCase() + fechaFormateada.slice(1);

  // Construir listado de paradas en el orden del mapa
  const lineasOrdenes = ordenes.map((orden, index) => {
    const ref = orden["full-reference"] || `#${orden.id}`;
    const descripcion = orden["private-comments"] || orden.descripcion || 'Sin descripción';
    const descripcionCorta = descripcion.length > 60 ? descripcion.substring(0, 60) + '...' : descripcion;
    const geocodificada = ordenesGeodificadas.find(og => og.id === orden.id);
    const direccion = geocodificada?.direccion || orden["customer-address"] || '';
    const direccionCorta = direccion.length > 50 ? direccion.substring(0, 50) + '...' : direccion;

    return `${index + 1}. *${ref}*\n   📍 ${direccionCorta || 'Dirección pendiente'}\n   📝 ${descripcionCorta}`;
  }).join('\n\n');

  // Construir URL de Google Maps con waypoints
  // Formato: origin=lat,lng&waypoints=lat,lng|lat,lng&destination=lat,lng
  const puntosConCoordenadas = ordenesGeodificadas.filter(og => og.lat && og.lng);
  let urlMaps = '';

  if (puntosConCoordenadas.length > 0) {
    const origin = `${puntosConCoordenadas[0].lat},${puntosConCoordenadas[0].lng}`;
    const destination = `${puntosConCoordenadas[puntosConCoordenadas.length - 1].lat},${puntosConCoordenadas[puntosConCoordenadas.length - 1].lng}`;
    
    if (puntosConCoordenadas.length > 2) {
      const waypoints = puntosConCoordenadas
        .slice(1, -1)
        .map(p => `${p.lat},${p.lng}`)
        .join('|');
      urlMaps = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
    } else if (puntosConCoordenadas.length === 2) {
      urlMaps = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    } else {
      urlMaps = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    }
  }

  const mensaje = `🗓️ *Ruta ${fechaCapitalizada}*
👨‍🔧 *Técnico: ${tecnico}*
📊 *${ordenes.length} orden${ordenes.length !== 1 ? 'es' : ''}*

─────────────────────

${lineasOrdenes}

─────────────────────
${urlMaps ? `\n🗺️ *Ruta Google Maps:*\n${urlMaps}` : '⚠️ Sin coordenadas disponibles para la ruta'}

¡Recuerda activar tempo! ⌛
_Climatización Zapata_`;



  return mensaje;
};

const WeeklyAgenda = () => {
  const { tecnicos } = useTecnicos();

  // Roster reactivo con la misma forma que las viejas constantes de constants.js,
  // para no tocar el resto de la vista:
  //   calendario     → { Lunes: { Benito: ['Zona 5'], ... }, ... }
  //   tecnicoColors  → { Benito: '#3B82F6', ..., Cliente: '#8B5CF6' }
  const calendario = useMemo(() => {
    const out = {};
    DIAS_SEMANA.forEach(dia => {
      out[dia] = {};
      tecnicos.forEach(t => {
        const zonas = t.zonas?.[dia] || [];
        if (zonas.length) out[dia][t.nombre] = zonas;
      });
    });
    return out;
  }, [tecnicos]);

  const tecnicoColors = useMemo(() => ({
    ...Object.fromEntries(tecnicos.map(t => [t.nombre, t.color])),
    Cliente: '#8B5CF6',
  }), [tecnicos]);

  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [workloadData, setWorkloadData] = useState({});
  const [installationData, setInstallationData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedTechnicianOrders, setSelectedTechnicianOrders] = useState(null);
  const [allWorkordersData, setAllWorkordersData] = useState([]);
  const [ordenesGeodificadas, setOrdenesGeodificadas] = useState([]);
  const [loadingMapa, setLoadingMapa] = useState(false);
  
  // Estados para WhatsApp
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedOrderForWhatsApp, setSelectedOrderForWhatsApp] = useState(null);

  // Modal de progreso de recordatorios
  const [progressModal, setProgressModal] = useState(null);
  const modalResolveRef = useRef(null);

  const showConfirmModal = (config) => new Promise(resolve => {
    modalResolveRef.current = resolve;
    setProgressModal({ ...config, type: 'confirm' });
  });

  const confirmModal = () => { modalResolveRef.current?.(true); };
  const cancelModal = () => { modalResolveRef.current?.(false); setProgressModal(null); };

  // Toggle mapa/lista en popup móvil
  const [popupTab, setPopupTab] = useState('lista');

  // 🆕 Estado para vista móvil
  const [isMobileView, setIsMobileView] = useState(false);

  // 🆕 Detectar tamaño de pantalla
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    checkMobileView();
    window.addEventListener('resize', checkMobileView);
    
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // ✅ NUEVO: Sincronizar orden de la lista con el orden optimizado del mapa
  useEffect(() => {
    if (!ordenesGeodificadas.length || !selectedTechnicianOrders) return;

    const ordenadoPorMapa = [...selectedTechnicianOrders.ordenes].sort((a, b) => {
      const idxA = ordenesGeodificadas.findIndex(og => og.id === a.id);
      const idxB = ordenesGeodificadas.findIndex(og => og.id === b.id);
      // Las órdenes sin geocodificar van al final
      const posA = idxA === -1 ? Infinity : idxA;
      const posB = idxB === -1 ? Infinity : idxB;
      return posA - posB;
    });

    setSelectedTechnicianOrders(prev => ({
      ...prev,
      ordenes: ordenadoPorMapa
    }));
  }, [ordenesGeodificadas]);

  // 🆕 FUNCIÓN AUXILIAR: Detectar si un teléfono es español
  const isSpanishPhone = (phone) => {
    if (!phone) return false;
    
    const cleaned = phone.replace(/\D/g, '');
    
    const isSpanish = cleaned.startsWith('34') || 
                     (!cleaned.startsWith('34') && cleaned.length === 9);
    
    console.log(`📞 Análisis teléfono: ${phone} -> ${cleaned} -> Español: ${isSpanish}`);
    return isSpanish;
  };

  // 🆕 FUNCIÓN AUXILIAR: Generar mensaje según idioma con estructura unificada
  const generateMessage = (cliente, tecnico, fecha, hora, isSpanish = true, direccion = '') => {
    const fechaFormateada = new Date(fecha).toLocaleDateString(isSpanish ? 'es-ES' : 'en-US');
    const empresaNombre = 'Climatización Zapata';
    const empresaWeb = 'https://climatizacionzapata.es/contacto/';
    
    if (isSpanish) {
      return `🔧 ***Recordatorio de Cita - ${empresaNombre}***

¡Hola ${cliente.displayName}! 👋

Te recordamos que tienes una cita programada:

📅 ***Fecha:*** ${fechaFormateada}
👨‍🔧 ***Técnico:*** ${tecnico}${direccion ? `\n📍 ***Dirección:*** ${direccion}` : ''}

Si necesitas cambiar la cita, por favor contáctanos.

¡Nos vemos! 😊
${empresaWeb}`;
    } else {
      return `🔧 ***Appointment Reminder - ${empresaNombre}***

Hello ${cliente.displayName}! 👋

We remind you that you have a scheduled appointment:

📅 ***Date:*** ${fechaFormateada}
👨‍🔧 ***Technician:*** ${tecnico}${direccion ? `\n📍 ***Address:*** ${direccion}` : ''}

If you need to change the appointment, please contact us.

See you! 😊
${empresaWeb}`;
    }
  };

  // Helpers compartidos para recordatorios
  const procesarInformacionCliente = (clienteData) => {
    if (!clienteData) return null;
    let displayName = clienteData["legal-name"]?.trim() || clienteData.name?.trim() || clienteData["full-reference"] || `Cliente ${clienteData.id}`;
    const telefono = clienteData.phone?.trim() || clienteData.phone2?.trim() || '';
    return { displayName, telefono };
  };

  const cargarClienteRemote = async (accountPath) => {
    try {
      const res = await fetch(`/.netlify/functions/getClient?accountPath=${encodeURIComponent(accountPath)}`, { headers: { ...authService.authHeader() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data.success && data.client ? procesarInformacionCliente(data.client) : null;
    } catch (err) {
      console.error(`❌ Error cargando cliente ${accountPath}:`, err);
      return null;
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/\D/g, '');
    if (!cleaned.startsWith('34') && cleaned.length === 9) cleaned = '34' + cleaned;
    if (cleaned.startsWith('340')) cleaned = '34' + cleaned.substring(3);
    return '+' + cleaned;
  };

  // 🆕 NUEVA FUNCIÓN: Recordatorios específicos para un técnico
  const handleTechnicianWhatsAppReminders = async (tecnico, fecha) => {
    const tecnicoId = tecnicoIds[tecnico];
    const ordenesDelTecnico = allWorkordersData.filter(wo =>
      (wo["assignee-id"] === tecnicoId || wo["agent-id"] === tecnicoId) &&
      wo.date === fecha && wo["account-path"]
    );

    if (ordenesDelTecnico.length === 0) {
      setProgressModal({
        type: 'info',
        title: 'Sin órdenes',
        message: `No hay órdenes con información de cliente para ${tecnico} el ${new Date(fecha + 'T12:00:00').toLocaleDateString('es-ES')}`,
      });
      return;
    }

    const confirmed = await showConfirmModal({
      title: `📱 Recordatorios — ${tecnico}`,
      message: `${ordenesDelTecnico.length} órdenes del ${new Date(fecha + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}`,
      detail: 'Se abrirán pestañas WhatsApp solo para clientes con teléfono válido.',
    });
    if (!confirmed) return;

    setProgressModal({ type: 'progress', title: `Cargando clientes de ${tecnico}...`, total: ordenesDelTecnico.length, processed: 0, items: [] });

    const BATCH_SIZE = 3;
    const ordenesParaProcesar = [];
    let sinTelefono = 0;

    try {
      for (let i = 0; i < ordenesDelTecnico.length; i += BATCH_SIZE) {
        const lote = ordenesDelTecnico.slice(i, i + BATCH_SIZE);
        const resultados = await Promise.all(lote.map(async (orden) => {
          const info = await cargarClienteRemote(orden["account-path"]);
          if (info?.telefono) {
            const tel = formatPhoneNumber(info.telefono);
            if (tel.length > 5) {
              const esEsp = isSpanishPhone(info.telefono);
              return { orden, cliente: info, telefono: tel, esEspañol: esEsp };
            }
          }
          sinTelefono++;
          return null;
        }));

        const validos = resultados.filter(Boolean);
        ordenesParaProcesar.push(...validos);

        setProgressModal(prev => ({
          ...prev,
          processed: Math.min(i + BATCH_SIZE, ordenesDelTecnico.length),
          items: [
            ...(prev.items || []),
            ...validos.map(v => ({ name: v.cliente.displayName, telefono: v.telefono, status: 'ok' })),
            ...resultados.filter(r => r === null && lote[resultados.indexOf(r)]).map(() => ({ name: '—', status: 'noPhone' })),
          ],
        }));

        if (i + BATCH_SIZE < ordenesDelTecnico.length) await new Promise(r => setTimeout(r, 500));
      }

      if (ordenesParaProcesar.length === 0) {
        setProgressModal({ type: 'info', title: 'Sin teléfonos', message: `Ningún cliente de ${tecnico} tiene teléfono válido.` });
        return;
      }

      const confirmed2 = await showConfirmModal({
        title: `📊 Resumen — ${tecnico}`,
        message: `${ordenesParaProcesar.length} clientes con teléfono válido · ${sinTelefono} sin teléfono`,
        detail: `¿Abrir WhatsApp para los ${ordenesParaProcesar.length} clientes?`,
        items: ordenesParaProcesar.map(v => ({ name: v.cliente.displayName, telefono: v.telefono, status: 'ok' })),
      });
      if (!confirmed2) return;

      setProgressModal({ type: 'progress', title: `Abriendo WhatsApp para ${tecnico}...`, total: ordenesParaProcesar.length, processed: 0, items: [] });

      ordenesParaProcesar.forEach((item, index) => {
        setTimeout(() => {
          const { orden, cliente, telefono, esEspañol } = item;
          const message = generateMessage(cliente, tecnico, fecha, orden.scheduledTime || '09:00', esEspañol, '');
          window.open(`https://api.whatsapp.com/send?phone=${telefono}&text=${encodeURIComponent(message)}`, '_blank');
          setProgressModal(prev => prev ? { ...prev, processed: index + 1, items: [...(prev.items || []), { name: cliente.displayName, telefono, status: 'sent' }] } : null);
        }, index * 1500);
      });

      setTimeout(() => {
        setProgressModal({ type: 'done', title: `✅ Completado — ${tecnico}`, stats: { ok: ordenesParaProcesar.length, noPhone: sinTelefono } });
      }, (ordenesParaProcesar.length * 1500) + 500);

    } catch (err) {
      console.error(`❌ Error recordatorios ${tecnico}:`, err);
      setProgressModal({ type: 'info', title: 'Error', message: `Error procesando recordatorios: ${err.message}` });
    }
  };

  // 🆕 FUNCIÓN PARA RECORDATORIOS MASIVOS
  const handleMassWhatsAppReminders = async () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowISO = obtenerFechaLocal(tomorrow);

    const ordenesMañana = allWorkordersData.filter(wo => wo.date === tomorrowISO && wo["account-path"]);

    if (ordenesMañana.length === 0) {
      setProgressModal({ type: 'info', title: 'Sin órdenes', message: 'No hay órdenes con información de cliente para mañana.' });
      return;
    }

    const confirmed = await showConfirmModal({
      title: '📱 Recordatorios masivos',
      message: `${ordenesMañana.length} órdenes para mañana ${tomorrow.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}`,
      detail: 'Se abrirán pestañas WhatsApp solo para clientes con teléfono válido. El proceso puede tomar unos minutos.',
    });
    if (!confirmed) return;

    setProgressModal({ type: 'progress', title: 'Cargando clientes...', total: ordenesMañana.length, processed: 0, items: [] });

    const BATCH_SIZE = 5;
    const ordenesParaProcesar = [];
    let sinTelefono = 0;

    try {
      for (let i = 0; i < ordenesMañana.length; i += BATCH_SIZE) {
        const lote = ordenesMañana.slice(i, i + BATCH_SIZE);
        const resultados = await Promise.all(lote.map(async (orden) => {
          const info = await cargarClienteRemote(orden["account-path"]);
          if (info?.telefono) {
            const tel = formatPhoneNumber(info.telefono);
            if (tel.length > 5) {
              return { orden, cliente: info, telefono: tel, esEspañol: isSpanishPhone(info.telefono) };
            }
          }
          sinTelefono++;
          return null;
        }));

        const validos = resultados.filter(Boolean);
        ordenesParaProcesar.push(...validos);

        setProgressModal(prev => ({
          ...prev,
          processed: Math.min(i + BATCH_SIZE, ordenesMañana.length),
          items: [...(prev.items || []), ...validos.map(v => ({ name: v.cliente.displayName, telefono: v.telefono, status: 'ok' }))],
        }));

        if (i + BATCH_SIZE < ordenesMañana.length) await new Promise(r => setTimeout(r, 1000));
      }

      if (ordenesParaProcesar.length === 0) {
        setProgressModal({ type: 'info', title: 'Sin teléfonos', message: 'Ningún cliente tiene teléfono válido registrado.' });
        return;
      }

      const confirmed2 = await showConfirmModal({
        title: '📊 Resumen del procesamiento',
        message: `${ordenesParaProcesar.length} clientes con teléfono · ${sinTelefono} sin teléfono`,
        detail: `¿Abrir WhatsApp para los ${ordenesParaProcesar.length} clientes?`,
        items: ordenesParaProcesar.map(v => ({ name: v.cliente.displayName, telefono: v.telefono, status: 'ok' })),
      });
      if (!confirmed2) return;

      setProgressModal({ type: 'progress', title: `Abriendo WhatsApp para ${ordenesParaProcesar.length} clientes...`, total: ordenesParaProcesar.length, processed: 0, items: [] });

      ordenesParaProcesar.forEach((item, index) => {
        setTimeout(() => {
          const { orden, cliente, telefono, esEspañol } = item;
          const nombreTecnico = getTechnicianNameById(orden["assignee-id"], orden["agent-id"]);
          const message = generateMessage(cliente, nombreTecnico, tomorrow, orden.scheduledTime || '09:00', esEspañol, '');
          window.open(`https://api.whatsapp.com/send?phone=${telefono}&text=${encodeURIComponent(message)}`, '_blank');
          setProgressModal(prev => prev ? { ...prev, processed: index + 1, items: [...(prev.items || []), { name: cliente.displayName, telefono, status: 'sent' }] } : null);
        }, index * 2000);
      });

      setTimeout(() => {
        setProgressModal({ type: 'done', title: '✅ Recordatorios enviados', stats: { ok: ordenesParaProcesar.length, noPhone: sinTelefono } });
      }, (ordenesParaProcesar.length * 2000) + 500);

    } catch (err) {
      console.error('❌ Error recordatorios masivos:', err);
      setProgressModal({ type: 'info', title: 'Error', message: `Error procesando recordatorios: ${err.message}` });
    }
  };

  // Funciones para WhatsApp
  const handleOpenWhatsAppModal = (orden, event) => {
    event?.stopPropagation();
    console.log('📱 Abriendo WhatsApp modal para:', orden);
    setSelectedOrderForWhatsApp(orden);
    setShowWhatsAppModal(true);
  };

  const closeWhatsAppModal = () => {
    setShowWhatsAppModal(false);
    setSelectedOrderForWhatsApp(null);
  };

  // Función para abrir popup con órdenes del técnico
  const handleTechnicianClick = async (tecnico, dia, dateISO) => {
    if (loading) return;
    
    const tecnicoId = tecnicoIds[tecnico];
    
    const ordenesDelTecnico = allWorkordersData.filter(wo => {
      const esDelTecnico = wo["assignee-id"] === tecnicoId || wo["agent-id"] === tecnicoId;
      const esDelDia = wo.date === dateISO;
      return esDelTecnico && esDelDia;
    });
    
    const ordenesConTecnico = ordenesDelTecnico.map(orden => ({
      ...orden,
      tecnico: tecnico
    }));
    
    console.log(`🔍 Mostrando órdenes de ${tecnico} para ${dia} (${dateISO}):`, ordenesConTecnico);
    
    setPopupTab('lista');
    setSelectedTechnicianOrders({
      tecnico,
      dia,
      fecha: dateISO,
      ordenes: ordenesConTecnico
    });

    setLoadingMapa(true);
    setOrdenesGeodificadas([]);

    try {
      console.log(`🗺️ Cargando órdenes de ${tecnico} para ${dateISO}`);
      
      const response = await fetch(
        `/.netlify/functions/geocodificarCita?fecha=${dateISO}&tecnico=${tecnico}`
      , { headers: { ...authService.authHeader() } });
      
      if (!response.ok) {
        throw new Error(`Error geocodificando: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("✅ Respuesta de geocodificación completa:", data);
      
      const ordenes = data.ordenes || [];
      console.log("📊 Órdenes recibidas:", ordenes.length);
      
      if (ordenes.length > 0) {
        console.log("📍 Primera orden como ejemplo:", ordenes[0]);
        
        const ordenesOrdenadas = ordenarOrdenesPorDistancia(ordenes);
        setOrdenesGeodificadas(ordenesOrdenadas);
      } else {
        console.warn("⚠️ No se encontraron órdenes geocodificadas");
        console.warn("🔍 Debug info:", data.debug);
        setError(`No se encontraron órdenes para ${tecnico} el ${dia}`);
      }
      
    } catch (err) {
      console.error("❌ Error cargando mapa:", err);
      setError(`Error cargando mapa: ${err.message}`);
    } finally {
      setLoadingMapa(false);
    }
  };

  // Función para cerrar popup
  const closeOrdersPopup = () => {
    setSelectedTechnicianOrders(null);
    setOrdenesGeodificadas([]);
    setLoadingMapa(false);
  };

  // Calcular centro del mapa
  const getMapCenter = () => {
    console.log("🎯 Calculando centro - ordenesGeodificadas:", ordenesGeodificadas.length);
    
    if (ordenesGeodificadas && ordenesGeodificadas.length > 0) {
      const validWorkorders = ordenesGeodificadas.filter(wo => wo.lat && wo.lng && !isNaN(wo.lat) && !isNaN(wo.lng));
      console.log("🎯 Órdenes válidas para centro:", validWorkorders.length);
      
      if (validWorkorders.length > 0) {
        const avgLat = validWorkorders.reduce((sum, wo) => sum + parseFloat(wo.lat), 0) / validWorkorders.length;
        const avgLng = validWorkorders.reduce((sum, wo) => sum + parseFloat(wo.lng), 0) / validWorkorders.length;
        const center = { lat: avgLat, lng: avgLng };
        console.log("🎯 Centro calculado:", center);
        return center;
      }
    }
    
    const defaultCenter = { lat: 37.9922, lng: -1.1307 };
    console.log("🎯 Usando centro por defecto:", defaultCenter);
    return defaultCenter;
  };

  // Preparar marcadores para el MapComponent
  const prepareMarkersForMap = () => {
    console.log("🗺️ Preparando marcadores - ordenesGeodificadas:", ordenesGeodificadas);
    
    if (!ordenesGeodificadas || ordenesGeodificadas.length === 0) {
      console.log("❌ No hay ordenesGeodificadas para preparar");
      return [];
    }
    
    const markers = ordenesGeodificadas
      .filter(orden => orden.lat && orden.lng && !isNaN(orden.lat) && !isNaN(orden.lng))
      .map((orden, index) => {
        console.log(`📍 Preparando marcador ${index + 1}:`, {
          id: orden.id,
          lat: orden.lat,
          lng: orden.lng,
          tecnico: orden.tecnico,
          direccion: orden.direccion
        });
        
        return {
          ...orden,
          lat: parseFloat(orden.lat),
          lng: parseFloat(orden.lng),
          tecnico: orden.tecnico || 'Desconocido',
          direccion: orden.direccion || `Orden ${orden.id}`,
          cp: orden.cp || '',
          id: orden.id
        };
      });
    
    console.log(`🗺️ Marcadores preparados: ${markers.length} de ${ordenesGeodificadas.length} órdenes`);
    return markers;
  };

  // Función para obtener el estado de una orden
  const getOrderStatus = (wo) => {
    const documentStateId = wo["document-state-id"];
    
    switch (documentStateId) {
      case 1305009:
        return { label: "Instalación", color: "bg-red-100 text-red-800 border-red-200", priority: 1 };
      case 1016219:
        return { label: "Cobrado", color: "bg-green-100 text-green-800 border-green-200", priority: 5 };
      case 651428:
        return { label: "Cerrado", color: "bg-green-100 text-green-800 border-green-200", priority: 5 };
      case 1018827:
        return { label: "En ruta", color: "bg-gray-100 text-gray-800 border-gray-200", priority: 2 };
      case 1069646:
        return { label: "Pre-liquidación NOFER", color: "bg-blue-100 text-blue-800 border-blue-200", priority: 4 };
      case 1069647:
        return { label: "Pre-liquidación SVAN", color: "bg-blue-100 text-blue-800 border-blue-200", priority: 4 };
      case 1070405:
        return { label: "Pre-liquidación LOGMAN", color: "bg-blue-100 text-blue-800 border-blue-200", priority: 4 };
      case 1041994:
        return { label: "Pre-liquidación ARISTON", color: "bg-blue-100 text-blue-800 border-blue-200", priority: 4 };
      case 1226452:
        return { label: "Pre-liquidación BERETTA", color: "bg-blue-100 text-blue-800 border-blue-200", priority: 4 };
      case 1069430:
        return { label: "PM", color: "bg-purple-100 text-purple-800 border-purple-200", priority: 4 };
      case 1004686:
        return { label: "Garantía", color: "bg-gray-100 text-gray-800 border-gray-200", priority: 2 };
      case 1042425:
        return { label: "Garantía 3", color: "bg-gray-200 text-gray-900 border-gray-300", priority: 2 };
      case 1073018:
        return { label: "Garantía G4", color: "bg-gray-200 text-gray-900 border-gray-300", priority: 2 };
      case 651457:
        return { label: "Pendiente", color: "bg-yellow-100 text-yellow-800 border-yellow-200", priority: 2 };
      case 1016220:
        return { label: "Ausente", color: "bg-purple-100 text-purple-800 border-purple-200", priority: 2 };
      case 1016222:
        return { label: "Pendiente de material", color: "bg-orange-100 text-orange-800 border-orange-200", priority: 2 };
      case 1040205:
        return { label: "Pendiente de presupuesto", color: "bg-orange-100 text-orange-800 border-orange-200", priority: 2 };
      case 1007173:
        return { label: "Pendiente de pago", color: "bg-red-100 text-red-800 border-red-200", priority: 2 };
      case 651446:
        return { label: "Rechazado", color: "bg-red-200 text-red-900 border-red-300", priority: 0 };
      case 1016221:
        return { label: "Pendiente de sustitución", color: "bg-indigo-100 text-indigo-800 border-indigo-200", priority: 3 };
      default:
        if (wo.status && wo.status !== "activo") {
          const status = wo.status.toLowerCase();
          if (["completado", "completed", "finalizado"].includes(status)) {
            return { label: "Completada", color: "bg-green-100 text-green-800 border-green-200", priority: 5 };
          }
          if (["en-progreso", "in-progress", "iniciado"].includes(status)) {
            return { label: "En progreso", color: "bg-blue-100 text-blue-800 border-blue-200", priority: 3 };
          }
          if (["cancelado", "cancelled", "rechazado"].includes(status)) {
            return { label: "Cancelado", color: "bg-red-200 text-red-900 border-red-300", priority: 0 };
          }
          return { 
            label: wo.status.charAt(0).toUpperCase() + wo.status.slice(1), 
            color: "bg-gray-100 text-gray-600 border-gray-200", 
            priority: 2 
          };
        }
        
        return { 
          label: `Estado ${documentStateId || 'desconocido'}`, 
          color: "bg-gray-100 text-gray-600 border-gray-200", 
          priority: 1 
        };
    }
  };

  // Función para formatear dirección
  const formatAddress = (addressPath) => {
    if (!addressPath) return "Dirección no disponible";
    const parts = addressPath.split('/').filter(part => part.trim());
    return parts.slice(-2).join(', ') || addressPath;
  };

  // Función para verificar si todas las órdenes de un técnico están cerradas
  const areAllOrdersClosed = (tecnico, dia, dateISO) => {
    const tecnicoId = tecnicoIds[tecnico];
    
    const ordenesDelTecnico = allWorkordersData.filter(wo => {
      const esDelTecnico = wo["assignee-id"] === tecnicoId || wo["agent-id"] === tecnicoId;
      const esDelDia = wo.date === dateISO;
      return esDelTecnico && esDelDia;
    });
    
    if (ordenesDelTecnico.length === 0) {
      return false;
    }
    
    const todasCerradas = ordenesDelTecnico.every(orden => {
      const status = getOrderStatus(orden);
      const estadosCerrados = [
        'Cerrado',
        'Cobrado', 
        'Completada',
        'Pre-liquidación NOFER',
        'Pre-liquidación SVAN',
        'Pre-liquidación LOGMAN',
        'Pre-liquidación ARISTON',
        'Pre-liquidación BERETTA'
      ];
      
      return estadosCerrados.includes(status.label);
    });
    
    console.log(`🔍 ${tecnico} - ${dia} (${dateISO}): ${ordenesDelTecnico.length} órdenes, todas cerradas: ${todasCerradas}`);
    return todasCerradas;
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const normalizeDayName = (date) => {
    const nombreDia = date.toLocaleDateString("es-ES", { weekday: "long" })
      .replace("é", "e").replace("í", "i").replace("á", "a").replace("ó", "o").replace("ú", "u");
    return nombreDia.charAt(0).toUpperCase() + nombreDia.slice(1);
  };

  // Obtener fechas de la semana seleccionada
  const getWeekDates = (date) => {
    console.log("🔧 Calculando fechas para:", date);
    
    const diasLaborables = [];
    let fechaActual = new Date(date);
    fechaActual.setHours(0, 0, 0, 0);
    
    const dayOfWeek = fechaActual.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    fechaActual.setDate(fechaActual.getDate() - daysToSubtract);
    
    console.log("📅 Lunes calculado:", fechaActual.toLocaleDateString('es-ES'));
    
    for (let i = 0; i < 5; i++) {
      const dia = new Date(fechaActual);
      dia.setDate(fechaActual.getDate() + i);
      diasLaborables.push(dia);
    }
    
    const weekDates = {};
    
    diasLaborables.forEach((fecha) => {
      const fechaKey = obtenerFechaLocal(fecha);
      const nombreDiaCapitalizado = normalizeDayName(fecha);
      
      weekDates[nombreDiaCapitalizado] = fechaKey;
      
      console.log(`📅 ${nombreDiaCapitalizado}: ${fechaKey} (${fecha.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })})`);
    });
    
    const ordenDias = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes'];
    const weekDatesOrdenado = {};
    
    ordenDias.forEach(dia => {
      if (weekDates[dia]) {
        weekDatesOrdenado[dia] = weekDates[dia];
      }
    });
    
    console.log("📅 Fechas calculadas de la semana (ordenadas):", weekDatesOrdenado);
    return weekDatesOrdenado;
  };

  // Función para verificar instalaciones
  const checkInstallations = async (weekDates) => {
    const newInstallationData = {};
    
    Object.keys(calendario).forEach(dia => {
      newInstallationData[dia] = {};
      Object.keys(calendario[dia]).forEach(tecnico => {
        newInstallationData[dia][tecnico] = false;
      });
    });

    try {
      const tecnicosIds = {
        Benito: 21936,
        Pablo: 23415,
        Dani: 21945,
        Ivan: 31171
      };

      console.log(`🔧 Consultando StelOrder directamente para verificar instalaciones...`);
      console.log(`📅 Fechas de la semana para instalaciones:`, weekDates);
      
      const response = await fetch(`/.netlify/functions/getStelOrderData`, { headers: { ...authService.authHeader() } });

      if (!response.ok) {
        console.warn(`⚠️ Error consultando StelOrder: ${response.status}`);
        return newInstallationData;
      }
      
      const data = await response.json();
      const allWorkorders = data.workorders || [];
      
      console.log(`📋 Total workorders obtenidos: ${allWorkorders.length}`);
      
      Object.entries(weekDates).forEach(([dayName, dateISO]) => {
        console.log(`🔧 Procesando ${dayName} (${dateISO})`);
        
        const workordersDelDia = allWorkorders.filter(wo => {
          if (!wo.date) return false;
          const fechaKey = obtenerFechaLocal(wo.date);
          const match = fechaKey === dateISO;
          if (match) {
            console.log(`  📅 Workorder ${wo.id} coincide: ${fechaKey} === ${dateISO}`);
          }
          return match;
        });
        
        console.log(`📅 ${dayName} (${dateISO}): ${workordersDelDia.length} workorders del día`);
        
        Object.entries(tecnicosIds).forEach(([tecnicoNombre, tecnicoId]) => {
          const tieneInstalacion = workordersDelDia.some(wo => {
            const esInstalacion = wo["document-state-id"] === 1305009;
            const esTecnicoPrincipal = wo["assignee-id"] === tecnicoId;
            const esAgente = wo["agent-id"] === tecnicoId;
            
            if (esInstalacion && (esTecnicoPrincipal || esAgente)) {
              console.log(`🔧 ${dayName} (${dateISO}): Instalación encontrada para ${tecnicoNombre}`, {
                workorderId: wo.id,
                documentStateId: wo["document-state-id"],
                assigneeId: wo["assignee-id"],
                agentId: wo["agent-id"],
                tecnicoId: tecnicoId,
                fecha: wo.date
              });
              return true;
            }
            return false;
          });
          
          if (tieneInstalacion && newInstallationData[dayName]?.hasOwnProperty(tecnicoNombre)) {
            newInstallationData[dayName][tecnicoNombre] = true;
            console.log(`✅ ${dayName} (${dateISO}): ${tecnicoNombre} (ID: ${tecnicoId}) CONFIRMADO con instalación`);
          }
        });
      });
      
      console.log(`🔧 Resultado final de instalaciones:`, newInstallationData);
      return newInstallationData;
      
    } catch (err) {
      console.error("❌ Error verificando instalaciones:", err);
      return newInstallationData;
    }
  };

  // Cargar datos reales de la semana
  const loadWeekData = useCallback(async () => {
    setLoading(true);
    setError("");
    
    try {
      console.log("📅 Cargando datos de la semana:", selectedWeek);
      
      const weekDates = getWeekDates(selectedWeek);
      console.log("🗓️ Fechas calculadas para carga de datos:", weekDates);
      
      const newWorkloadData = {};
      
      Object.keys(calendario).forEach(dia => {
        newWorkloadData[dia] = {};
        Object.keys(calendario[dia]).forEach(tecnico => {
          newWorkloadData[dia][tecnico] = 0;
        });
      });

      console.log("📡 Obteniendo datos de StelOrder...");
      const response = await fetch(`/.netlify/functions/getStelOrderData`, { headers: { ...authService.authHeader() } });

      if (!response.ok) {
        throw new Error(`Error obteniendo datos: ${response.status}`);
      }
      
      const data = await response.json();
      const allWorkorders = data.workorders || [];
      
      console.log(`📋 Total workorders obtenidos: ${allWorkorders.length}`);
      
      Object.entries(weekDates).forEach(([dayName, dateISO]) => {
        console.log(`📅 Procesando ${dayName} (${dateISO})`);
        
        Object.entries(tecnicoIds).forEach(([tecnicoNombre, tecnicoId]) => {
          const workordersDelTecnico = allWorkorders.filter(wo => {
            const esAsignado = wo["assignee-id"] === tecnicoId;
            const esAgente = wo["agent-id"] === tecnicoId;
            const esMismaFecha = wo.date === dateISO;
            
            if ((esAsignado || esAgente) && esMismaFecha) {
              console.log(`📋 ${dayName} - ${tecnicoNombre}: WO ${wo.id}`, {
                fecha: wo.date,
                assignee: wo["assignee-id"],
                agent: wo["agent-id"],
                estado: wo["document-state-id"]
              });
            }
            
            return (esAsignado || esAgente) && esMismaFecha;
          });
          
          const conteo = workordersDelTecnico.length;
          console.log(`📊 ${dayName} - ${tecnicoNombre} (ID: ${tecnicoId}): ${conteo} órdenes`);
          
          if (newWorkloadData[dayName]?.hasOwnProperty(tecnicoNombre)) {
            newWorkloadData[dayName][tecnicoNombre] = conteo;
          }
        });
      });
      
      console.log("🔧 Verificando instalaciones de la semana...");
      const installations = await checkInstallations(weekDates);
      
      setWorkloadData(newWorkloadData);
      setInstallationData(installations);
      setAllWorkordersData(allWorkorders);
      setLastUpdated(new Date());
      console.log("✅ Datos de la semana cargados:", newWorkloadData);
      console.log("✅ Instalaciones detectadas:", installations);
      
    } catch (err) {
      console.error("❌ Error cargando datos de la semana:", err);
      setError(`Error cargando datos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedWeek, calendario]);

  // Cargar datos cuando cambia la semana
  useEffect(() => {
    loadWeekData();
  }, [loadWeekData]);

  const formatWeek = (date) => {
    const fechaActual = new Date(date);
    fechaActual.setHours(0, 0, 0, 0);
    
    const dayOfWeek = fechaActual.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    fechaActual.setDate(fechaActual.getDate() - daysToSubtract);
    
    const startOfWeek = new Date(fechaActual);
    const endOfWeek = new Date(fechaActual);
    endOfWeek.setDate(startOfWeek.getDate() + 4);
    
    return `${startOfWeek.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}`;
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedWeek);
    newDate.setDate(selectedWeek.getDate() + (direction * 7));
    setSelectedWeek(newDate);
  };

  const isDayFullyLoaded = (dia) => {
    const dayData = workloadData[dia];
    if (!dayData) return false;
    const tecns = Object.values(dayData);
    return tecns.length > 0 && tecns.every(load => load >= 6);
  };

  const getWorkloadStatus = (technicianLoad, allClosed = false) => {
    if (allClosed && technicianLoad > 0) {
      return { color: 'bg-green-100 text-green-700', cellBg: 'bg-green-50 border-green-300', label: 'Ruta Cerrada' };
    }
    if (technicianLoad === 0) return { color: 'bg-gray-100 text-gray-500', cellBg: 'bg-white border-gray-200', label: 'Libre' };
    if (technicianLoad <= 3) return { color: 'bg-green-100 text-green-700', cellBg: 'bg-green-50 border-green-200', label: 'Disponible' };
    if (technicianLoad <= 5) return { color: 'bg-yellow-100 text-yellow-700', cellBg: 'bg-yellow-50 border-yellow-200', label: 'Ocupado' };
    return { color: 'bg-red-100 text-red-700', cellBg: 'bg-red-50 border-red-300', label: 'Completo' };
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border p-3 sm:p-4 md:p-6">
      {/* 📱 HEADER RESPONSIVE */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
        <div className="w-full sm:w-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Icons.Calendar className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="truncate">Agenda Semanal</span>
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-red-500"></div>
            )}
          </h2>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            {isMobileView ? 'Planificación técnicos' : 'Planificación y carga de trabajo por técnico'}
            {lastUpdated && !isMobileView && (
              <span className="text-xs text-gray-500 ml-2">
                (Actualizado: {lastUpdated.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })})
              </span>
            )}
          </p>
        </div>
        
        {/* 📱 CONTROLES RESPONSIVE */}
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigateWeek(-1)}
              className="p-1.5 sm:p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
              disabled={loading}
              aria-label="Semana anterior"
            >
              <Icons.ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            
            <div className="bg-red-50 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg border border-red-200 text-center">
              <span className="font-semibold text-red-800 text-xs sm:text-sm">{formatWeek(selectedWeek)}</span>
              <div className="text-red-500 text-xs mt-0.5 hidden sm:block">
                Semana {getWeekNumber(selectedWeek)} · {selectedWeek.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
              </div>
            </div>
            
            <button 
              onClick={() => navigateWeek(1)}
              className="p-1.5 sm:p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
              disabled={loading}
              aria-label="Semana siguiente"
            >
              <Icons.ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* 📱 Botón de recordatorios - más pequeño en móvil */}
            <button
              onClick={handleMassWhatsAppReminders}
              className="p-1.5 sm:p-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-colors duration-200 disabled:opacity-50 flex items-center gap-1 text-xs sm:text-sm"
              disabled={loading}
              title="Recordatorios WhatsApp para mañana"
            >
              <span className="text-sm sm:text-base">📱</span>
              <span className="hidden sm:inline">Recordatorios</span>
            </button>

            {/* 📱 Botón de actualizar */}
            <button
              onClick={() => {
                console.log("🔄 Recargando agenda - fecha actual:", selectedWeek);
                loadWeekData();
              }}
              className="p-1.5 sm:p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors duration-200 disabled:opacity-50 text-sm sm:text-base"
              disabled={loading}
              title="Actualizar datos"
            >
              🔄
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6 text-xs sm:text-sm">
          ❌ {error}
          <button 
            onClick={loadWeekData}
            className="ml-2 sm:ml-4 px-2 sm:px-3 py-1 bg-red-600 text-white rounded text-xs sm:text-sm hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Leyenda compacta */}
      <div className="flex flex-wrap items-center gap-2 mb-4 px-1">
        {[
          { dot: 'bg-gray-300', label: 'Libre', tip: '0 órdenes' },
          { dot: 'bg-green-400', label: 'Disponible', tip: '1-3 órdenes' },
          { dot: 'bg-yellow-400', label: 'Ocupado', tip: '4-5 órdenes' },
          { dot: 'bg-red-500', label: 'Completo', tip: '6+ órdenes' },
          { dot: 'bg-green-500 ring-2 ring-green-300', label: 'Ruta cerrada', tip: 'Todas las órdenes finalizadas' },
        ].map(item => (
          <span key={item.label} className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2 py-1 rounded-full" title={item.tip}>
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item.dot}`} />
            {item.label}
          </span>
        ))}
        {!loading && Object.keys(workloadData).length > 0 && (
          <span className="ml-auto text-xs text-green-600 font-medium">● Tiempo real</span>
        )}
      </div>

      {/* Estado de carga */}
      {loading && (
        <div className="bg-red-50 border border-red-200 p-4 sm:p-6 rounded-xl mb-4 sm:mb-6 text-center">
          <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-red-500 mx-auto mb-3 sm:mb-4"></div>
          <p className="text-red-800 font-medium text-sm sm:text-base">Cargando datos de StelOrder...</p>
          <p className="text-red-600 text-xs sm:text-sm mt-1">Consultando órdenes de trabajo</p>
        </div>
      )}

      {/* 📱 CALENDARIO RESPONSIVE */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
        {Object.entries(calendario).map(([dia, tecnicos]) => {
          const diaCompleto = !loading && isDayFullyLoaded(dia);
          return (
          <div key={dia} className={`bg-gradient-to-b border rounded-xl p-2 sm:p-3 md:p-4 shadow-sm transition-all ${
            diaCompleto ? 'from-red-50 to-red-50/50 border-red-300 shadow-red-100' : 'from-gray-50 to-white border-gray-200'
          }`}>
            <h3 className={`font-bold mb-2 sm:mb-3 md:mb-4 text-center border-b pb-2 text-sm sm:text-base flex items-center justify-center gap-1 ${
              diaCompleto ? 'text-red-700 border-red-200' : 'text-gray-800 border-gray-200'
            }`}>
              {dia}
              {diaCompleto && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold ml-1">Completo</span>}
            </h3>
            
            <div className="space-y-2 sm:space-y-3">
              {Object.entries(tecnicos).map(([tecnico, zonas]) => {
                const load = workloadData[dia]?.[tecnico] || 0;
                const hasInstallation = installationData[dia]?.[tecnico] || false;
                const dateISO = getWeekDates(selectedWeek)[dia];
                const allOrdersClosed = areAllOrdersClosed(tecnico, dia, dateISO);
                const status = getWorkloadStatus(load, allOrdersClosed);
                
                return (
                  <div key={tecnico} className="group cursor-pointer">
                    <div
                      className={`relative rounded-lg p-2 sm:p-3 border transition-all duration-200 hover:shadow-md ${
                        loading ? 'bg-white border-gray-100 opacity-75' :
                        status.cellBg + ' hover:brightness-95'
                      }`}
                      onClick={() => handleTechnicianClick(tecnico, dia, dateISO)}
                    >
                      
                      {/* Indicador visual de ruta cerrada */}
                      {allOrdersClosed && !loading && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                          <span className="text-white text-xs font-bold">✓</span>
                        </div>
                      )}
                      
                      {/* Header del técnico */}
                      <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <div 
                            className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: tecnicoColors[tecnico] }}
                          ></div>
                          <span className="font-semibold text-xs sm:text-sm text-gray-800 truncate">{tecnico}</span>
                          {allOrdersClosed && !loading && (
                            <span className="text-green-600 font-bold text-xs sm:text-sm">✓</span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          {/* 🎯 Botón de recordatorios por técnico */}
                          {!loading && load > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTechnicianWhatsAppReminders(tecnico, dateISO);
                              }}
                              className="w-4 h-4 sm:w-5 sm:h-5 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs flex items-center justify-center transition-colors shadow-sm"
                              title={`Recordatorios WhatsApp para ${tecnico} - ${dia}`}
                            >
                              🎯
                            </button>
                          )}
                          
                          <div className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-medium ${status.color} ${
                            loading ? 'animate-pulse' : ''
                          }`}>
                            {loading ? '...' : `${load}/6`}
                          </div>
                        </div>
                      </div>
                      
                      {/* Zonas asignadas - ocultas en móvil pequeño */}
                      {!isMobileView && (
                        <div className="space-y-1">
                          {zonas.map((zona, idx) => (
                            <div key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded truncate">
                              📍 {zona}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Indicadores especiales */}
                      {hasInstallation && !loading && (
                        <div className="mt-1.5 sm:mt-2 text-xs bg-red-50 text-red-700 px-2 py-1 rounded border border-red-200 truncate">
                          🔧 {isMobileView ? 'Instalación' : 'Instalación programada'}
                        </div>
                      )}
                      
                      {allOrdersClosed && !loading && (
                        <div className="mt-1.5 sm:mt-2 text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 truncate">
                          ✅ {isMobileView ? 'Completada' : 'Ruta completada'}
                        </div>
                      )}
                      
                      {/* Barra de progreso */}
                      <div className="mt-2 sm:mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                          <div 
                            className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                              loading ? 'bg-gray-300 animate-pulse' :
                              allOrdersClosed ? 'bg-green-500' :
                              load === 0 ? 'bg-gray-300' :
                              load <= 2 ? 'bg-red-400' :
                              load <= 4 ? 'bg-yellow-400' :
                              load <= 6 ? 'bg-red-500' : 'bg-red-600'
                            }`}
                            style={{ width: loading ? '50%' : `${Math.min((load / 6) * 100, 100)}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1 text-center">
                          {loading ? 'Cargando...' : status.label}
                        </div>
                      </div>
                      
                      {!loading && load > 0 && !allOrdersClosed && (
                        <div className="text-xs text-red-600 mt-1.5 sm:mt-2 text-center">
                          📊 {load} órdenes
                        </div>
                      )}
                      
                      {!loading && allOrdersClosed && (
                        <div className="text-xs text-green-600 mt-1.5 sm:mt-2 text-center">
                          🎉 {load} cerradas
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ); })}
      </div>

      {/* RESUMEN SEMANAL ENRIQUECIDO */}
      <div className="mt-4 sm:mt-6 bg-gradient-to-r from-red-50 to-gray-50 rounded-xl p-3 sm:p-4 border border-red-200">
        <h3 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">📊 Resumen Semanal</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
          {Object.keys(tecnicoColors).filter(t => t !== 'Cliente').map(tecnico => {
            const totalOrders = Object.values(workloadData).reduce((sum, day) => sum + (day?.[tecnico] || 0), 0);
            const diasConInstalacion = Object.values(installationData).filter(day => day?.[tecnico]).length;

            const ordenesDelTecnico = allWorkordersData.filter(wo => {
              const id = tecnicoIds[tecnico];
              return wo["assignee-id"] === id || wo["agent-id"] === id;
            });
            const CERRADOS = ['Cerrado','Cobrado','Completada','Pre-liquidación NOFER','Pre-liquidación SVAN','Pre-liquidación LOGMAN','Pre-liquidación ARISTON','Pre-liquidación BERETTA'];
            const cerradas = ordenesDelTecnico.filter(wo => CERRADOS.includes(getOrderStatus(wo).label)).length;
            const pendientes = ordenesDelTecnico.filter(wo => !CERRADOS.includes(getOrderStatus(wo).label)).length;
            const pctCierre = totalOrders > 0 ? Math.round((cerradas / totalOrders) * 100) : 0;

            return (
              <div key={tecnico} className={`bg-white rounded-xl p-3 border shadow-sm ${loading ? 'animate-pulse' : ''}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tecnicoColors[tecnico] }} />
                  <span className="font-semibold text-sm truncate">{tecnico}</span>
                  {!loading && diasConInstalacion > 0 && (
                    <span className="ml-auto bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded-full">🔧{diasConInstalacion}</span>
                  )}
                </div>
                <div className="text-2xl font-bold text-gray-900">{loading ? '…' : totalOrders}</div>
                <div className="text-xs text-gray-500 mb-2">órdenes esta semana</div>
                {!loading && totalOrders > 0 && (
                  <>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1.5">
                      <div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${pctCierre}%` }} />
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-green-600 font-medium">✓ {cerradas} cerradas</span>
                      <span className="text-amber-600 font-medium">{pendientes} pend.</span>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{pctCierre}% cierre</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer con información */}
      <div className="mt-3 sm:mt-4 text-center text-xs text-gray-500">
        🔄 {isMobileView ? 'Datos de StelOrder' : 'Datos actualizados automáticamente desde StelOrder'}
        {lastUpdated && !isMobileView && (
          <span className="ml-2">
            | Última actualización: {lastUpdated.toLocaleString('es-ES')}
          </span>
        )}
      </div>

      {/* 📱 POPUP DE ÓRDENES DEL TÉCNICO RESPONSIVE */}
      {selectedTechnicianOrders && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
            {/* Header del popup */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-3 sm:p-4 md:p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div 
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-white flex-shrink-0"
                    style={{ backgroundColor: tecnicoColors[selectedTechnicianOrders.tecnico] }}
                  ></div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold truncate">
                      {selectedTechnicianOrders.tecnico}
                    </h2>
                    <p className="text-red-100 text-xs sm:text-sm truncate">
                      {selectedTechnicianOrders.dia} - {new Date(selectedTechnicianOrders.fecha).toLocaleDateString('es-ES', { 
                        day: 'numeric', 
                        month: isMobileView ? 'short' : 'long',
                        year: isMobileView ? undefined : 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                  <div className="text-right">
                    <div className="text-2xl sm:text-3xl font-bold">
                      {selectedTechnicianOrders.ordenes.length}
                    </div>
                    <div className="text-red-100 text-xs sm:text-sm">
                      orden{selectedTechnicianOrders.ordenes.length !== 1 ? 'es' : ''}
                    </div>
                  </div>
                  <button 
                    onClick={closeOrdersPopup}
                    className="p-1.5 sm:p-2 hover:bg-red-800 rounded-lg transition-colors"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs móvil */}
            {isMobileView && (
              <div className="flex border-b border-gray-200 bg-gray-50">
                {['lista', 'mapa'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setPopupTab(tab)}
                    className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                      popupTab === tab ? 'text-red-600 border-b-2 border-red-600 bg-white' : 'text-gray-500'
                    }`}
                  >
                    {tab === 'lista' ? '📋 Órdenes' : '🗺️ Mapa'}
                  </button>
                ))}
              </div>
            )}

            {/* 📱 Contenido del popup - layout responsive */}
            <div className="flex flex-col lg:flex-row h-[calc(95vh-200px)] sm:h-[70vh]">
              {/* Mapa */}
              <div className={`${isMobileView ? (popupTab === 'mapa' ? 'flex-1' : 'hidden') : 'lg:w-2/3'} p-2 sm:p-4 border-r border-gray-200`}>
                <div className="h-full bg-gray-100 rounded-xl overflow-hidden relative">
                  {loadingMapa && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-red-500 mx-auto mb-3 sm:mb-4"></div>
                        <p className="text-gray-600 text-sm sm:text-base">Cargando ubicaciones...</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="h-full">
                    <MapComponent 
                      key={`${selectedTechnicianOrders?.tecnico}-${selectedTechnicianOrders?.fecha}-${ordenesGeodificadas.length}`}
                      center={getMapCenter()}
                      markers={prepareMarkersForMap()}
                      clientMarker={null}
                      loading={loadingMapa}
                    />
                  </div>
                  
                  {!loadingMapa && ordenesGeodificadas.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                      <div className="text-center p-4">
                        <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">📍</div>
                        <div className="text-lg sm:text-xl font-semibold text-gray-800">
                          Sin ubicaciones
                        </div>
                        <div className="text-gray-600 mt-2 text-sm sm:text-base">
                          No se pudieron geocodificar las direcciones
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Lista de órdenes */}
              <div className={`${isMobileView ? (popupTab === 'lista' ? 'flex-1' : 'hidden') : 'lg:w-1/3'} p-2 sm:p-4 flex flex-col`}>
                <div className="flex items-center gap-2 mb-3 sm:mb-4">
                  <h3 className="font-semibold text-gray-800 text-sm sm:text-base flex items-center gap-2 flex-1 min-w-0">
                    📋 Órdenes del día
                  </h3>
                  {/* 🆕 BOTÓN: Enviar ruta por WhatsApp */}
                  {ordenesGeodificadas.length > 0 && selectedTechnicianOrders && (
                    <button
                      onClick={() => {
                        const mensaje = generarMensajeRuta(
                          selectedTechnicianOrders.tecnico,
                          selectedTechnicianOrders.fecha,
                          selectedTechnicianOrders.ordenes,
                          ordenesGeodificadas
                        );
                        const phone = tecnicoTelefonos[selectedTechnicianOrders.tecnico] || '';
                        const url = `https://api.whatsapp.com/send?${phone ? `phone=${phone}&` : ''}text=${encodeURIComponent(mensaje)}`;
                        window.open(url, '_blank');
                      }}
                      className="flex-shrink-0 flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors shadow-sm"
                      title={`Enviar ruta por WhatsApp a ${selectedTechnicianOrders?.tecnico}`}
                    >
                      <span>📲</span>
                      <span className="hidden sm:inline">Enviar ruta</span>
                    </button>
                  )}
                </div>
                
                {selectedTechnicianOrders.ordenes.length === 0 ? (
                  <div className="text-center py-6 sm:py-8">
                    <div className="text-3xl sm:text-4xl mb-2">📝</div>
                    <h4 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">Sin órdenes</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      No hay órdenes programadas para este día.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 overflow-y-auto pr-1 sm:pr-2 space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                      {selectedTechnicianOrders.ordenes.map((orden, index) => {
                        const status = getOrderStatus(orden);
                        const esInstalacion = orden["document-state-id"] === 1305009;
                        const ordenGeodificada = ordenesGeodificadas.find(og => og.id === orden.id);
                        const posicionEnRuta = ordenesGeodificadas.findIndex(og => og.id === orden.id);
                        
                        return (
                          <div 
                            key={orden.id} 
                            className={`relative border rounded-lg p-2 sm:p-3 transition-all duration-200 hover:shadow-sm ${
                              esInstalacion ? 'border-red-300 bg-red-50' : 
                              status.priority >= 4 ? 'border-green-200 bg-green-50' :
                              status.priority <= 1 ? 'border-gray-200 bg-gray-50' :
                              'border-gray-200 bg-white hover:bg-gray-50'
                            }`}
                          >
                            {/* Header de la orden con botones de WhatsApp */}
                            <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                              <div className="flex items-center gap-1 sm:gap-2 flex-1 min-w-0">
                                {/* 🆕 Número de parada en ruta */}
                                {posicionEnRuta !== -1 && (
                                  <div className="w-5 h-5 bg-blue-600 text-white rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0">
                                    {posicionEnRuta + 1}
                                  </div>
                                )}
                                <div className="bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-mono truncate">
                                  {orden["full-reference"] || `#${orden.id}`}
                                </div>
                                {esInstalacion && (
                                  <div className="bg-red-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-bold flex-shrink-0">
                                    🔧
                                  </div>
                                )}
                                {status.priority >= 4 && (
                                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-green-500 flex-shrink-0" title="Alta prioridad"></div>
                                )}
                                {status.priority <= 1 && (
                                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gray-400 flex-shrink-0" title="Baja prioridad"></div>
                                )}
                              </div>
                              
                              {/* BOTONES DE WHATSAPP */}
                              <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
                                <WhatsAppQuickButtons orden={orden} className="scale-90 sm:scale-100" />
                                
                                <button
                                  onClick={(e) => handleOpenWhatsAppModal(orden, e)}
                                  className="w-6 h-6 sm:w-7 sm:h-7 bg-green-500 hover:bg-green-600 text-white rounded text-xs transition-colors shadow-sm flex items-center justify-center flex-shrink-0"
                                  title="Abrir WhatsApp completo"
                                >
                                  📱+
                                </button>
                                
                                <div className="text-xs text-gray-500 ml-0.5 sm:ml-1 flex-shrink-0">
                                  {index + 1}/{selectedTechnicianOrders.ordenes.length}
                                </div>
                              </div>
                            </div>

                            {/* ESTADO */}
                            <div className={`inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs font-medium border mb-2 sm:mb-3 ${status.color}`}>
                              {status.label}
                            </div>

                            {/* DESCRIPCIÓN */}
                            <div className="text-xs sm:text-sm text-gray-700 mb-1.5 sm:mb-2">
                              <strong>📝 {isMobileView ? 'Desc:' : 'Descripción:'}</strong>
                              <div className="text-xs text-gray-600 mt-1 italic line-clamp-2">
                                {orden["private-comments"] || orden.descripcion || 'Sin descripción'}
                              </div>
                            </div>

                            {/* Información del cliente */}
                            {(orden["customer-phone"] || orden.customerPhone) && (
                              <div className="text-xs text-gray-600 mb-1.5 sm:mb-2 bg-green-50 border border-green-200 rounded p-1.5 sm:p-2">
                                📱 <strong>{orden["customer-name"] || orden.customerName || 'Cliente'}</strong>
                                <br/>📞 {orden["customer-phone"] || orden.customerPhone}
                                {(orden["customer-address"] || orden.customerAddress) && !isMobileView && (
                                  <>
                                    <br/>📍 {orden["customer-address"] || orden.customerAddress}
                                  </>
                                )}
                              </div>
                            )}

                            {/* Estado de geocodificación */}
                            <div className="text-xs text-gray-500 mb-1.5 sm:mb-2">
                              {ordenGeodificada ? (
                                <span className="text-green-600">✅ {isMobileView ? 'En mapa' : 'Ubicada en mapa'}</span>
                              ) : (
                                <span className="text-orange-600">⚠️ Sin geocodificar</span>
                              )}
                            </div>

                            {/* ICONO DE FIRMADO */}
                            {(orden.signed === "true" || orden.signed === true) && (
                              <div className="absolute bottom-1.5 sm:bottom-2 right-1.5 sm:right-2">
                                <div 
                                  className="w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center shadow-md"
                                  title="Orden firmada"
                                >
                                  <span className="text-white text-xs font-bold">✓</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* RESUMEN FIJO ABAJO */}
                    <div className="bg-gray-50 rounded-lg border p-2 sm:p-3 mt-auto flex-shrink-0">
                      <h4 className="font-semibold text-gray-800 text-xs sm:text-sm mb-1.5 sm:mb-2 flex items-center gap-2">
                        📊 Resumen
                        <div className="ml-auto text-xs text-gray-500">
                          {selectedTechnicianOrders.dia}
                        </div>
                      </h4>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{isMobileView ? 'Total:' : 'Total órdenes:'}</span>
                          <span className="font-semibold">{selectedTechnicianOrders.ordenes.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">WhatsApp:</span>
                          <span className="font-semibold text-green-600">
                            {selectedTechnicianOrders.ordenes.filter(o => o["customer-phone"] || o.customerPhone).length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{isMobileView ? 'Inst.:' : 'Instalaciones:'}</span>
                          <span className="font-semibold text-red-600">
                            {selectedTechnicianOrders.ordenes.filter(o => o["document-state-id"] === 1305009).length}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">{isMobileView ? 'OK:' : 'Completadas:'}</span>
                          <span className="font-semibold text-green-600">
                            {selectedTechnicianOrders.ordenes.filter(o => {
                              const status = getOrderStatus(o);
                              return status.priority >= 4;
                            }).length}
                          </span>
                        </div>
                        {!isMobileView && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Pendientes:</span>
                              <span className="font-semibold text-yellow-600">
                                {selectedTechnicianOrders.ordenes.filter(o => {
                                  const status = getOrderStatus(o);
                                  return status.priority === 2;
                                }).length}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">En mapa:</span>
                              <span className="font-semibold text-blue-600">
                                {prepareMarkersForMap().length}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* 📱 Footer del popup responsive */}
            <div className="border-t border-gray-200 px-3 sm:px-4 md:px-6 py-3 sm:py-4 bg-gray-50">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
                <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                  {isMobileView ? (
                    <>
                      {selectedTechnicianOrders.ordenes.length} órdenes | 
                      {selectedTechnicianOrders.ordenes.filter(o => o["customer-phone"] || o.customerPhone).length} WhatsApp | 
                      {selectedTechnicianOrders.ordenes.filter(o => getOrderStatus(o).priority >= 4).length} OK
                    </>
                  ) : (
                    <>
                      🗺️ Google Maps | 
                      📊 {selectedTechnicianOrders.ordenes.length} órdenes | 
                      📱 {selectedTechnicianOrders.ordenes.filter(o => o["customer-phone"] || o.customerPhone).length} con WhatsApp |
                      🔧 {selectedTechnicianOrders.ordenes.filter(o => o["document-state-id"] === 1305009).length} instalaciones |
                      ✅ {selectedTechnicianOrders.ordenes.filter(o => getOrderStatus(o).priority >= 4).length} completadas
                    </>
                  )}
                </div>
                <button
                  onClick={closeOrdersPopup}
                  className="bg-red-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:bg-red-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE PROGRESO DE RECORDATORIOS */}
      {progressModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-5 py-4">
              <h3 className="font-bold text-base">{progressModal.title}</h3>
              {progressModal.message && <p className="text-green-100 text-sm mt-1">{progressModal.message}</p>}
            </div>

            <div className="p-5">
              {/* Confirm phase */}
              {progressModal.type === 'confirm' && (
                <>
                  {progressModal.detail && <p className="text-sm text-gray-600 mb-4">{progressModal.detail}</p>}
                  {progressModal.items && progressModal.items.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-lg mb-4">
                      {progressModal.items.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-2 border-b last:border-0 text-sm">
                          <span className="text-green-500">✓</span>
                          <span className="flex-1 truncate text-gray-700">{item.name}</span>
                          <span className="text-xs text-gray-400">{item.telefono}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button onClick={cancelModal} className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                      Cancelar
                    </button>
                    <button
                      onClick={() => { confirmModal(); setProgressModal(null); }}
                      className="flex-1 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
                    >
                      Confirmar
                    </button>
                  </div>
                </>
              )}

              {/* Progress phase */}
              {progressModal.type === 'progress' && (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600 flex-shrink-0" />
                    <span className="text-sm text-gray-600">
                      {progressModal.processed} / {progressModal.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                    <div
                      className="h-2 rounded-full bg-green-500 transition-all duration-300"
                      style={{ width: progressModal.total > 0 ? `${(progressModal.processed / progressModal.total) * 100}%` : '0%' }}
                    />
                  </div>
                  {progressModal.items && progressModal.items.length > 0 && (
                    <div className="max-h-36 overflow-y-auto border rounded-lg">
                      {progressModal.items.slice(-8).map((item, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-0 text-xs">
                          <span className={item.status === 'sent' ? 'text-blue-500' : item.status === 'ok' ? 'text-green-500' : 'text-gray-300'}>
                            {item.status === 'sent' ? '📤' : item.status === 'ok' ? '✓' : '—'}
                          </span>
                          <span className="flex-1 truncate text-gray-700">{item.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Info / error */}
              {progressModal.type === 'info' && (
                <>
                  <p className="text-sm text-gray-600 mb-4">{progressModal.message}</p>
                  <button onClick={() => setProgressModal(null)} className="w-full py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors">
                    Cerrar
                  </button>
                </>
              )}

              {/* Done */}
              {progressModal.type === 'done' && (
                <>
                  <div className="text-center py-2 mb-4">
                    <div className="text-4xl mb-2">🎉</div>
                    {progressModal.stats && (
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><span className="font-semibold text-green-600">📱 {progressModal.stats.ok}</span> mensajes enviados</p>
                        {progressModal.stats.noPhone > 0 && <p className="text-gray-400">{progressModal.stats.noPhone} sin teléfono</p>}
                      </div>
                    )}
                  </div>
                  <button onClick={() => setProgressModal(null)} className="w-full py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors">
                    Cerrar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE WHATSAPP DIRECTO */}
      {showWhatsAppModal && selectedOrderForWhatsApp && (
        <WhatsAppDirect
          orden={selectedOrderForWhatsApp}
          onClose={closeWhatsAppModal}
        />
      )}
    </div>
  );
};

export default WeeklyAgenda;